/**
 * DeviceSmsPage — a dedicated, conversation-style viewer for a device's
 * collected SMS (UPGRADE-03). Reached from the device detail page's monitoring
 * card ("Open SMS conversations →").
 *
 * The reveal endpoint is keyset-paginated (newest-first, 200/page); this page
 * accumulates pages with useInfiniteQuery and groups the loaded messages by the
 * other party's number into conversation threads — an inbox on the left, a
 * chat transcript on the right. "Load older messages" fetches the next page;
 * older messages merge into their threads as they arrive.
 *
 * Privacy — THIS IS A CHILD'S MESSAGE CONTENT. The route is protected and the
 * view is additionally gated client- and server-side on `device.monitoring.view`;
 * EVERY page fetch is written to the audit chain (count-only) by the backend.
 */
import { Fragment, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { Device } from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { FormError, apiErrorMessage } from "../components/Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { cn, formatDate, formatDateTime, formatTime } from "../lib/utils.js";

interface SmsLogView {
  readonly id: string;
  readonly direction: string;
  readonly address: string;
  readonly body: string;
  readonly sentAt: string;
}
interface SmsLogsPage {
  readonly deviceId: string;
  readonly count: number;
  readonly returned: number;
  readonly records: readonly SmsLogView[];
  readonly nextCursor: string | null;
}

const PAGE_SIZE = 200;

interface Thread {
  readonly address: string;
  readonly messages: readonly SmsLogView[];
  readonly lastAt: string;
  readonly lastBody: string;
}

/** Group the loaded flat message list into per-number threads, newest thread first. */
function buildThreads(records: readonly SmsLogView[]): readonly Thread[] {
  const byAddress = new Map<string, SmsLogView[]>();
  for (const r of records) {
    const key = r.address.trim() || "(unknown)";
    const bucket = byAddress.get(key);
    if (bucket) bucket.push(r);
    else byAddress.set(key, [r]);
  }
  const threads: Thread[] = [];
  for (const [address, msgs] of byAddress) {
    // Oldest→newest within a thread (chat order).
    const ordered = [...msgs].sort((a, b) => a.sentAt.localeCompare(b.sentAt));
    const last = ordered[ordered.length - 1]!;
    threads.push({ address, messages: ordered, lastAt: last.sentAt, lastBody: last.body });
  }
  // Most recently active thread first.
  threads.sort((a, b) => b.lastAt.localeCompare(a.lastAt));
  return threads;
}

export function DeviceSmsPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const canView = auth.user?.permissions.includes("device.monitoring.view") ?? false;
  const [selected, setSelected] = useState<string | null>(null);

  const deviceQuery = useQuery({
    queryKey: ["devices", "byId", id],
    queryFn: () => api.get<Device>(`/api/devices/${id}`),
    enabled: canView,
  });

  const smsQuery = useInfiniteQuery({
    queryKey: ["devices", "smsLogs", id],
    queryFn: ({ pageParam }) =>
      api.get<SmsLogsPage>(
        `/api/devices/${id}/sms-logs?limit=${PAGE_SIZE}` +
          (pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: canView,
  });

  const records = useMemo(
    () => smsQuery.data?.pages.flatMap((p) => p.records) ?? [],
    [smsQuery.data],
  );
  const threads = useMemo(() => buildThreads(records), [records]);
  const total = smsQuery.data?.pages[0]?.count ?? 0;
  const loaded = records.length;

  // Default the open conversation to the most recent thread once data lands.
  const activeAddress = selected ?? threads[0]?.address ?? null;
  const activeThread = threads.find((t) => t.address === activeAddress) ?? null;

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SMS conversations</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-simuflex-slate">
            You don&apos;t have permission to view monitoring data (
            <code>device.monitoring.view</code>).
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link
            to="/devices/$id"
            params={{ id }}
            className="text-xs text-simuflex-slate hover:underline"
          >
            ← Back to device
          </Link>
          <h1 className="mt-1 text-lg font-semibold">SMS conversations</h1>
          <p className="text-xs text-simuflex-slate">
            {deviceQuery.data ? (
              <>
                {deviceQuery.data.model} ·{" "}
                <span className="font-mono">{deviceQuery.data.imei}</span>
              </>
            ) : (
              <span className="font-mono">{id}</span>
            )}
          </p>
        </div>
        <Badge tone="warning">audited reveal</Badge>
      </div>

      <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Message content is encrypted at rest and decrypted only here, under{" "}
        <code>device.monitoring.view</code>.{" "}
        <strong>Every load is written to the audit log.</strong> This may be a child&apos;s message
        content — view only when necessary.
      </p>

      <FormError message={smsQuery.isError ? apiErrorMessage(smsQuery.error) : null} />

      {smsQuery.isLoading ? (
        <Card>
          <CardBody>
            <p className="text-sm text-simuflex-slate">Decrypting messages…</p>
          </CardBody>
        </Card>
      ) : total === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-simuflex-slate">No SMS collected for this device yet.</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-simuflex-slate">
            <span>
              {threads.length} conversation(s) · {loaded.toLocaleString()} of{" "}
              {total.toLocaleString()} message(s) loaded
            </span>
            {smsQuery.hasNextPage ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={smsQuery.isFetchingNextPage}
                onClick={() => void smsQuery.fetchNextPage()}
              >
                {smsQuery.isFetchingNextPage ? "Loading…" : "Load older messages"}
              </Button>
            ) : (
              <span>All messages loaded</span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
            {/* Thread inbox */}
            <Card className={cn(activeThread && "hidden md:block")}>
              <CardBody className="max-h-[70vh] space-y-1 overflow-y-auto p-2">
                {threads.map((t) => (
                  <button
                    key={t.address}
                    type="button"
                    onClick={() => setSelected(t.address)}
                    className={cn(
                      "w-full rounded-lg px-3 py-2 text-left transition-colors",
                      t.address === activeAddress
                        ? "bg-simuflex-green/10"
                        : "hover:bg-simuflex-mist/60",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-sm font-medium">{t.address}</span>
                      <span className="shrink-0 text-[10px] text-simuflex-slate">
                        {formatDate(t.lastAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-simuflex-slate">{t.lastBody}</span>
                      <span className="shrink-0 rounded-full bg-simuflex-mist px-1.5 text-[10px] text-simuflex-slate">
                        {t.messages.length}
                      </span>
                    </div>
                  </button>
                ))}
              </CardBody>
            </Card>

            {/* Conversation transcript */}
            <Card className={cn(!activeThread && "hidden md:block")}>
              <CardHeader className="flex items-center justify-between gap-2">
                <CardTitle>
                  <span className="font-mono">
                    {activeThread?.address ?? "Select a conversation"}
                  </span>
                </CardTitle>
                {activeThread ? (
                  <button
                    type="button"
                    className="text-xs text-simuflex-slate hover:underline md:hidden"
                    onClick={() => setSelected(null)}
                  >
                    ← Conversations
                  </button>
                ) : null}
              </CardHeader>
              <CardBody className="max-h-[70vh] space-y-2 overflow-y-auto">
                {activeThread ? (
                  activeThread.messages.map((m, i) => {
                    const outbound = m.direction.toLowerCase() === "outbound";
                    // Day separator whenever the calendar day changes (messages
                    // are oldest→newest), so the date is visible in-conversation.
                    const prev = i > 0 ? activeThread.messages[i - 1] : undefined;
                    const showDay = !prev || formatDate(prev.sentAt) !== formatDate(m.sentAt);
                    return (
                      <Fragment key={m.id}>
                        {showDay ? (
                          <div className="flex justify-center py-1">
                            <span className="rounded-full bg-simuflex-mist px-2 py-0.5 text-[10px] text-simuflex-slate">
                              {formatDate(m.sentAt)}
                            </span>
                          </div>
                        ) : null}
                        <div className={cn("flex", outbound ? "justify-end" : "justify-start")}>
                          <div
                            className={cn(
                              "max-w-[80%] rounded-2xl px-3 py-2 text-sm",
                              outbound
                                ? "rounded-br-sm bg-simuflex-green text-white"
                                : "rounded-bl-sm bg-simuflex-mist text-simuflex-navy",
                            )}
                          >
                            <p className="whitespace-pre-wrap break-words">{m.body}</p>
                            <p
                              className={cn(
                                "mt-1 text-right text-[10px]",
                                outbound ? "text-white/70" : "text-simuflex-slate",
                              )}
                              title={formatDateTime(m.sentAt)}
                            >
                              {formatTime(m.sentAt)}
                            </p>
                          </div>
                        </div>
                      </Fragment>
                    );
                  })
                ) : (
                  <p className="text-sm text-simuflex-slate">
                    Select a conversation to read its messages.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
